import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuditLog } from '../../services/adminService';

/**
 * Phase 8 — read-only viewer for the AdminAuditLog collection.
 * Table of recent rows with optional filters (admin email substring,
 * target type, date range, limit). Click a row to expand inline and
 * see Before/After/Metadata JSON plus ClientIp + TraceId.
 */

const TARGET_TYPES = [
  { value: '', label: '— Toate target-urile —' },
  { value: 'User', label: 'User' },
  { value: 'InviteRequest', label: 'InviteRequest' },
  { value: 'Subscription', label: 'Subscription' },
];

const LIMIT_OPTIONS = [50, 100, 200, 500];

const ACTION_LABELS = {
  'user.confirm-email': 'Email confirmat (forțat)',
  'user.send-reset-password': 'Reset parolă trimis',
  'user.role-update': 'Roluri modificate',
};

/** Mirror of the BE MapActionLabel — used as fallback if the BE row didn't
 *  ship a precomputed actionLabel (older rows, or before this phase shipped). */
const mapActionLabel = (action) => {
  if (!action) return '—';
  if (ACTION_LABELS[action]) return ACTION_LABELS[action];
  if (action.startsWith('lead.')) return 'Acțiune lead';
  if (action.startsWith('bulk.')) return 'Operație în bloc';
  return action;
};

const fmtAbsolute = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');
  } catch {
    return iso;
  }
};

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `acum ${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `acum ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `acum ${hr}h`;
  const day = Math.round(hr / 24);
  if (day < 30) return `acum ${day}z`;
  const mon = Math.round(day / 30);
  if (mon < 12) return `acum ${mon} luni`;
  const yr = Math.round(mon / 12);
  return `acum ${yr} ani`;
};

const truncate = (text, max = 80) => {
  if (!text) return '';
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
};

/** Pretty-print JSON-as-string for the expansion panel. Tolerant of
 *  already-stringified payloads that aren't strict JSON. */
const prettyJson = (raw) => {
  if (raw == null || raw === '') return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const AdminAudit = () => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Pending filter inputs (edited freely) and the committed filters that
  // drive the load. Submit copies pending → committed, mirroring AdminUsers.
  const [actorEmailInput, setAdminEmailInput] = useState('');
  const [targetTypeInput, setTargetTypeInput] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [limitInput, setLimitInput] = useState(100);

  const [committed, setCommitted] = useState({
    actorEmail: '',
    targetType: '',
    from: '',
    to: '',
    limit: 100,
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // The BE filters by actorUserId, not email — for now we pass the
      // email substring as a client-side filter applied AFTER the fetch.
      // Server still gets the date range / targetType / limit narrowing.
      const data = await getAuditLog({
        targetType: committed.targetType || null,
        from: committed.from || null,
        to: committed.to || null,
        limit: committed.limit || 100,
      });
      let items = data.items || [];
      if (committed.actorEmail) {
        const needle = committed.actorEmail.trim().toLowerCase();
        items = items.filter((r) => (r.actorEmail || '').toLowerCase().includes(needle));
      }
      setRows(items);
      setCount(items.length);
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la încărcarea jurnalului de audit.');
      setRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [committed]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = (e) => {
    e.preventDefault();
    setExpandedId(null);
    setCommitted({
      actorEmail: actorEmailInput.trim(),
      targetType: targetTypeInput,
      from: fromInput,
      to: toInput,
      limit: Number(limitInput) || 100,
    });
  };

  const onReset = () => {
    setAdminEmailInput('');
    setTargetTypeInput('');
    setFromInput('');
    setToInput('');
    setLimitInput(100);
    setExpandedId(null);
    setCommitted({ actorEmail: '', targetType: '', from: '', to: '', limit: 100 });
  };

  const headerTitle = useMemo(() => `Jurnal de audit (${count})`, [count]);

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>{headerTitle}</h2>
      </div>

      <form className="admin-toolbar" onSubmit={onSubmit}>
        <input
          type="search"
          placeholder="Caută după email admin"
          value={actorEmailInput}
          onChange={(e) => setAdminEmailInput(e.target.value)}
        />
        <select
          value={targetTypeInput}
          onChange={(e) => setTargetTypeInput(e.target.value)}
        >
          {TARGET_TYPES.map((t) => (
            <option key={t.value || 'all'} value={t.value}>{t.label}</option>
          ))}
        </select>
        <label style={{ fontSize: 12, color: '#64748b' }}>
          De la:&nbsp;
          <input
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
          />
        </label>
        <label style={{ fontSize: 12, color: '#64748b' }}>
          Până la:&nbsp;
          <input
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
          />
        </label>
        <select
          value={limitInput}
          onChange={(e) => setLimitInput(Number(e.target.value))}
          title="Număr maxim de rânduri"
        >
          {LIMIT_OPTIONS.map((n) => (
            <option key={n} value={n}>Limit {n}</option>
          ))}
        </select>
        <button type="submit" className="admin-button">Reîncarcă</button>
        <button type="button" className="admin-button secondary" onClick={onReset}>
          Resetează
        </button>
      </form>

      {loading ? (
        <div className="admin-empty">Se încarcă…</div>
      ) : error ? (
        <div className="admin-empty">⚠️ {error}</div>
      ) : rows.length === 0 ? (
        <div className="admin-empty">Nu există rânduri de audit pentru criteriile alese.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Când</th>
                <th>Admin</th>
                <th>Acțiune</th>
                <th>Target</th>
                <th>Motiv</th>
                <th>Rezultat</th>
                <th>Trace</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isOpen = expandedId === r.id;
                const label = r.actionLabel || mapActionLabel(r.action);
                const beforePretty = prettyJson(r.beforeJson);
                const afterPretty = prettyJson(r.afterJson);
                const metaPretty = prettyJson(r.metadataJson);
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : r.id)}
                      style={{ cursor: 'pointer' }}
                      title={isOpen ? 'Click pentru a închide' : 'Click pentru detalii'}
                    >
                      <td>
                        <span title={fmtAbsolute(r.timestamp)}>{fmtRelative(r.timestamp)}</span>
                      </td>
                      <td>
                        {r.actorEmail || '—'}
                        {r.actorUserId && (
                          <>
                            <br />
                            <small style={{ color: '#94a3b8' }}>{r.actorUserId}</small>
                          </>
                        )}
                      </td>
                      <td title={r.action || ''}>{label}</td>
                      <td>
                        <strong>{r.targetType || '—'}</strong>
                        {r.targetId && (
                          <>
                            {' · '}
                            <code style={{ fontSize: 11, color: '#475569' }}>{r.targetId}</code>
                          </>
                        )}
                      </td>
                      <td title={r.reason || ''} style={{ maxWidth: 240 }}>
                        {truncate(r.reason, 60)}
                      </td>
                      <td>
                        {r.success ? (
                          <span className="admin-status converted" title="Succes">✓</span>
                        ) : (
                          <span
                            className="admin-status rejected"
                            title={r.errorMessage || 'Eșec'}
                          >
                            ✗
                          </span>
                        )}
                      </td>
                      <td>
                        {r.traceId ? (
                          <code style={{ fontSize: 11, color: '#475569' }}>
                            {truncate(r.traceId, 12)}
                          </code>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={7} style={{ background: '#f8fafc' }}>
                          <div style={{ display: 'grid', gap: 12, padding: '8px 4px' }}>
                            <div style={{ fontSize: 12, color: '#475569' }}>
                              <strong>Acțiune (brută):</strong>{' '}
                              <code>{r.action || '—'}</code>
                              {' · '}
                              <strong>Timestamp:</strong> {fmtAbsolute(r.timestamp)}
                              {' · '}
                              <strong>IP:</strong> {r.clientIp || '—'}
                              {' · '}
                              <strong>Trace:</strong>{' '}
                              <code>{r.traceId || '—'}</code>
                            </div>
                            {r.reason && (
                              <div style={{ fontSize: 13 }}>
                                <strong>Motiv complet:</strong>
                                <div
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: 8,
                                    marginTop: 4,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {r.reason}
                                </div>
                              </div>
                            )}
                            {!r.success && r.errorMessage && (
                              <div style={{ fontSize: 13 }}>
                                <strong style={{ color: '#b91c1c' }}>Mesaj eroare:</strong>
                                <pre
                                  style={{
                                    background: '#fef2f2',
                                    border: '1px solid #fecaca',
                                    borderRadius: 6,
                                    padding: 8,
                                    marginTop: 4,
                                    overflowX: 'auto',
                                    fontSize: 12,
                                  }}
                                >
                                  {r.errorMessage}
                                </pre>
                              </div>
                            )}
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                gap: 12,
                              }}
                            >
                              <div>
                                <strong style={{ fontSize: 12 }}>BeforeJson</strong>
                                <pre
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: 8,
                                    marginTop: 4,
                                    overflowX: 'auto',
                                    fontSize: 12,
                                    minHeight: 40,
                                  }}
                                >
                                  {beforePretty || '—'}
                                </pre>
                              </div>
                              <div>
                                <strong style={{ fontSize: 12 }}>AfterJson</strong>
                                <pre
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: 8,
                                    marginTop: 4,
                                    overflowX: 'auto',
                                    fontSize: 12,
                                    minHeight: 40,
                                  }}
                                >
                                  {afterPretty || '—'}
                                </pre>
                              </div>
                              <div>
                                <strong style={{ fontSize: 12 }}>MetadataJson</strong>
                                <pre
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 6,
                                    padding: 8,
                                    marginTop: 4,
                                    overflowX: 'auto',
                                    fontSize: 12,
                                    minHeight: 40,
                                  }}
                                >
                                  {metaPretty || '—'}
                                </pre>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminAudit;
